/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useContext, useEffect, useMemo, Key, useCallback } from 'react'
import { Modal, Tooltip, Table, Spin, Button, Select, Row, Tag } from 'antd'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { SelectValue } from 'antd/lib/select'
import {
  EditOutlined,
  EyeOutlined,
  FileAddFilled,
  FilterOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import { useQueryClient } from 'react-query'
import { useLocation } from 'react-router-dom'
import { ColumnsType } from 'antd/es/table'

import '@styles'
// eslint-disable-next-line import/order
import { theme } from '@styles'

import {
  useCreateDriver,
  useCreateTrailer,
  useCreateTruck,
  useEquipment,
  useGetTickets,
  useMaterials,
  usePagination,
  useTickets,
  useTicketsById,
  useUpdateDriver,
  useUpdateTicket,
  useUpdateTruck,
  useUpdateTrailer,
  useUserResponseManager,
  useConfigurationProperties,
  useCarrierVehicles,
} from '@hooks'
import {
  Filters,
  StatusTicketEnum,
  Ticket,
  Material,
  Equipment,
  Observation,
  PATHS,
  Truck,
  UpdateTruckData,
  Driver,
  UpdateDriverData,
  UpdateTicket,
  Trailer,
  EquipmentType,
  Vehicles,
  Vehicle,
} from '@types'
import { AcceptIcon, DeclineIcon } from '@icons'
import { TicketNotifContext, useFilters, useGeneral, useUser } from '@contexts'
import { QUERY_KEYS } from '@api'
import { DateFromToFilter, DeliveryNote, TIME } from '@components'

import { useSocket } from '../../../hooks/useSocket'
import DriverBarlight from '../../../components/DriverBarlight'
import prepareDataToExpandableTable from '../../../utils/prepare-data-to-expandable-table'
import { useTrucks } from '../../../hooks/useTrucks/index'
import { useTrailers } from '../../../hooks/useTrailers/index'
import { useDrivers } from '../../../hooks/useDrivers/index'
import CollapseExpand from '../../../components/CollapseExpand'
import { useHandleTicketModal } from '../../../contexts/HandleTicketModalContext'
import {
  ContentContainer,
  StyledButton,
  StyledSelect,
  StyledRow,
  ActionsCellContainer,
} from '../../../components/StyledComponents'
import {
  DeclineTicketModal,
  AcceptTicketModal,
  ActionButton,
  StyledLink,
  ActionButtonCustom,
  AddTruckModal,
  EditTruckModal,
  AddDriverModal,
  EditDriverModal,
} from './components'
import { AddAdditionalDataTicketModal } from './components/AddAdditionalDataTicketModal'
import { MapsModal } from '../../../components/MapsModal/MapsModal'
import { AddTrailerModal } from './components/AddTrailerModal'
import { EditTrailerModal } from './components/EditTrailerModal'
import ExpandableTable from '../../../components/ExpandableTable'

type VehicleKindFilter = 'truck' | 'trailer'

const isVehicleTruck = (vehicle: Vehicle): vehicle is Truck =>
  Object.prototype.hasOwnProperty.call(vehicle, 'straightTruck')

const TplDashboard = () => {
  const { t } = useTranslation()
  const { ticketById, isTicketByIdLoading } = useTicketsById()
  const [isMapsModalOpen, setIsMapsModalOpen] = useState(false)
  const [locationLatitude, setLocationLatitude] = useState<number | undefined>(undefined)
  const [locationLongitude, setLocationLongitude] = useState<number | undefined>(undefined)
  const [locationStreet, setLocationStreet] = useState<string[] | string | undefined>(undefined)
  const [locationCity, setLocationCity] = useState<string[] | string | undefined>(undefined)
  const [locationState, setLocationState] = useState<string[] | string | undefined>(undefined)
  const [locationNumber, setLocationNumber] = useState<string[] | string | undefined>(undefined)
  const [tag, setTag] = useState<string | undefined>(undefined)
  const [updateDataTrucks, setUpdateDataTrucks] = useState<Truck[]>([])
  const [updateDataDriver, setUpdateDataDriver] = useState<Driver>()
  const [idEditDataTruck, setIdEditDataTruck] = useState<number | undefined>(undefined)
  const [idEditDataDriver, setIdEditDataDriver] = useState<number>()
  const [idEditDataTrailer, setIdEditDataTrailer] = useState<number | undefined>(undefined)
  const [updateDataTrailers, setUpdateDataTrailers] = useState<Vehicles>([])
  const [sortOrder, setSortOrder] = useState<SelectValue>()
  const [vehicleEquipmentFilter, setVehicleEquipmentFilter] = useState<number[]>([])
  const [vehicleKindFilter, setVehicleKindFilter] = useState<VehicleKindFilter | undefined>()
  const [vehicleSort, setVehicleSort] = useState<string | undefined>()
  const [statusFilter, setStatusFilter] = useState<boolean | undefined>(true)
  const [isLoading, setIsLoading] = useState(false)
  const [addAdditionalDataLoading, setAddAdditionalDataLoading] = useState(false)

  const queryClient = useQueryClient()
  const { filters, setFilters, resetFilters } = useFilters()
  const { pathname } = useLocation()
  const { ticketToEdit, setTicketToEdit } = useHandleTicketModal()
  const ticketNotification = useContext(TicketNotifContext)
  const {
    setTicketToSearch,
    filtersDisplayed,
    handleDisplayFilters,
    menuDisplayed,
    ticketToSearch,
  } = useGeneral()

  const { createTruck, isLoadingTruck } = useCreateTruck()
  const { createTrailer, isLoadingTrailer } = useCreateTrailer()
  const { createDriver } = useCreateDriver()
  const { updateTruck } = useUpdateTruck()
  const { updateTrailer } = useUpdateTrailer()
  const { updateDriver } = useUpdateDriver()
  const { state: user } = useUser()
  const { requireTruckAndTrailer, requireDriver } = useConfigurationProperties()
  const { userIsLoading, userFetching } = useUserResponseManager(user?.id)
  const carrierId = user?.userDetail?.carrier?.id
  const isVehiclesPage = pathname === PATHS.CARRIER.VEHICLES
  const {
    vehiclesData,
    trucks: carrierTrucks,
    trailers: carrierTrailers,
    isLoading: carrierVehiclesLoading,
  } = useCarrierVehicles({
    carrierId,
    equipmentIds: vehicleEquipmentFilter,
    vehicleKind: vehicleKindFilter,
    statusFilter,
    enabled: isVehiclesPage && !!carrierId,
  })

  const driversData = useMemo(
    () =>
      user?.userDetail?.carrier?.drivers.map(driver => ({
        enabled: driver.disableAt === null,
        ...driver,
      })),
    [user],
  )

  const { PENDING, CONFIRMED, TRANSIT, DELIVERED, NO_SHOW } = StatusTicketEnum
  const declineButton = [PENDING, CONFIRMED]
  const today = new Date()
  const { EVOUCHERS, ALL, HOME } = PATHS.CARRIER
  const byDayPaths = [EVOUCHERS, ALL]
  const {
    declineTicket,
    acceptTicket,
    isAcceptTicketModalOpen,
    setIsAcceptTicketModalOpen,
    isDeclineTicketModalOpen,
    setIsDeclineTicketModalOpen,
    acceptTicketLoading,
    declineTicketLoading,
  } = useTickets()
  const {
    isAddTruckModalOpen,
    setIsAddTruckModalOpen,
    isEditTruckModalOpen,
    setIsEditTruckModalOpen,
  } = useTrucks()

  const {
    isAddTrailerModal,
    setIsAddTrailerModal,
    isEditTrailerModalOpen,
    setIsEditTrailerModalOpen,
  } = useTrailers()

  const {
    isAddDriverModalOpen,
    setIsAddDriverModalOpen,
    isEditDriverModalOpen,
    setIsEditDriverModalOpen,
  } = useDrivers()
  const { tickets, ticketsIsLoading } = useGetTickets(filters)

  // Conexión WebSocket y actualizaciones de tickets
  const { connect, socket } = useSocket()

  // Conectar a WebSocket cuando el componente se monta
  useEffect(() => {
    connect()
  }, [connect])

  // Escuchar actualizaciones de tickets directamente en el socket
  useEffect(() => {
    if (!socket) return

    const handleTicketUpdate = () => {
      queryClient.invalidateQueries([QUERY_KEYS.TICKETS])
      queryClient.invalidateQueries([QUERY_KEYS.TICKET])
      queryClient.invalidateQueries([QUERY_KEYS.TO_AUTHORIZE])
    }

    socket.on('ticket-update', handleTicketUpdate)

    return () => {
      socket.off('ticket-update', handleTicketUpdate)
    }
  }, [socket, queryClient])

  const { isAddAditionalDataTicketModalOpen, setIsAddAditionalDataTicketModalOpen } =
    useTicketsById()

  const { materialsOptions } = useMaterials()
  const { equipmentOptions } = useEquipment()
  const { Option } = Select
  const paginationTickets = usePagination({ filters, setFilters }, tickets?.total)

  const { updateTicket } = useUpdateTicket()
  const dateHourFormat = 'DD-MM-YYYY, HH:mm'

  const preparedData = useMemo(
    () => prepareDataToExpandableTable(tickets?.tickets) ?? [],
    [tickets],
  )

  useEffect(() => {
    if (ticketToSearch && tickets?.total === 1) {
      const [ticket] = tickets?.tickets
      if (ticket) {
        setFilters((prevState: Filters) => ({
          ...prevState,
          dateFrom: dayjs(tickets?.tickets[0].withdrawalDate).startOf('d').toDate(),
          dateTo: dayjs(tickets?.tickets[0].withdrawalDate).endOf('d').toDate(),
        }))
      }
      handleSetRowData([tickets.tickets[0].withdrawalDay!])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets])

  useEffect(() => {
    const timeout = setTimeout(() => {
      queryClient.fetchQuery(QUERY_KEYS.USER)
      queryClient.invalidateQueries(QUERY_KEYS.CARRIER_TRUCKS)
      queryClient.invalidateQueries(QUERY_KEYS.CARRIER_TRAILERS)
    }, 1000)
    return () => {
      clearTimeout(timeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditTruckModalOpen, isAddTruckModalOpen, isAddTrailerModal, isEditTrailerModalOpen])
  const actionButtons = (ticket: Ticket) => {
    const status = ticket.status.name
    const statusValuesVisibleToAddAditionalData = [CONFIRMED, NO_SHOW]
    return (
      <ActionsCellContainer>
        {status === PENDING && (
          <Tooltip title={t('globals.generics.accept')} placement="left">
            <ActionButton
              className="accept"
              onClick={() => {
                setIsLoading(true)
                queryClient.removeQueries([QUERY_KEYS.TICKET])
                setTicketToEdit(ticket.id)
                setTimeout(() => {
                  setIsLoading(false)
                  setIsAcceptTicketModalOpen(true)
                }, 500)
                ticketNotification.setState(ticket.id)
              }}
            >
              <AcceptIcon />
            </ActionButton>
          </Tooltip>
        )}
        <>
          {statusValuesVisibleToAddAditionalData.includes(status as StatusTicketEnum) && (
            <Tooltip title={t('globals.generics.invoice')} placement="left">
              <ActionButton
                className="invoice"
                onClick={() => {
                  setIsLoading(true)
                  queryClient.removeQueries([QUERY_KEYS.TICKET])
                  setTicketToEdit(ticket.id)
                  setTimeout(() => {
                    setIsLoading(false)
                    setIsAddAditionalDataTicketModalOpen(true)
                  }, 500)
                  ticketNotification.setState(ticket.id)
                }}
              >
                <FileAddFilled />
              </ActionButton>
            </Tooltip>
          )}
          {new Date(ticket?.withdrawalDate) > today &&
            declineButton.includes(status as StatusTicketEnum) && (
              <Tooltip title={t('globals.generics.decline')} placement="bottomRight">
                <ActionButton
                  className="decline"
                  onClick={() => {
                    setIsLoading(true)
                    queryClient.removeQueries([QUERY_KEYS.TICKET])
                    setTicketToEdit(ticket.id)
                    setTimeout(() => {
                      setIsLoading(false)
                      setIsDeclineTicketModalOpen(true)
                    }, 500)
                    ticketNotification.setState(ticket.id)
                  }}
                >
                  <DeclineIcon />
                </ActionButton>
              </Tooltip>
            )}
          {[TRANSIT, DELIVERED].includes(status as StatusTicketEnum) && (
            <Tooltip title={t('globals.generics.invoice')} placement="left">
              <ActionButton
                className="invoice"
                onClick={() => {
                  setIsLoading(true)
                  queryClient.removeQueries([QUERY_KEYS.TICKET])
                  setTicketToEdit(ticket.id)
                  setTimeout(() => {
                    setIsLoading(false)
                    setIsAddAditionalDataTicketModalOpen(true)
                  }, 500)
                  ticketNotification.setState(ticket.id)
                }}
              >
                <EyeOutlined />
              </ActionButton>
            </Tooltip>
          )}
        </>
      </ActionsCellContainer>
    )
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFilterOptions = (input: string, option: any) => {
    if (option.label) return option.label.toString().toLowerCase().includes(input.toLowerCase())
    return true
  }
  const seeComments = (comments: string) => {
    Modal.info({
      title: t('tpl_router.tpl_dashboard.comment_modal.title'),
      content: (
        <div>
          {comments ? (
            <p>{comments}</p>
          ) : (
            <p>
              <i>{t('tpl_router.tpl_dashboard.comment_modal.no_comments')}</i>
            </p>
          )}
        </div>
      ),
      okText: t('tpl_router.tpl_dashboard.comment_modal.close_button'),
    })
  }

  const columnsVehicle: ColumnsType = [
    {
      title: t('tpl_dashboard.equipmentType'),
      dataIndex: 'equipmentType',
      key: 'equipmentType',
      render: (_equipmentType: EquipmentType, record: Vehicle) =>
        !!record?.hasOwnProperty('straightTruck') ? 'Unidad Motriz' : 'Acoplado',
    },
    {
      title: t('tpl_dashboard.licensePlate'),
      dataIndex: 'licensePlate',
      key: 'licensePlate',
    },
    {
      title: t('tpl_dashboard.description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('tpl_dashboard.equipment'),
      dataIndex: 'equipmentType',
      key: 'equipmentType',
      render: (equipment: Equipment) => (
        <Tag
          style={{ color: theme.textColor }}
          color={equipment ? `#${equipment.color}` : theme.white}
        >
          {equipment ? equipment.nameShort : 'Sin equipo'}
        </Tag>
      ),
    },
    {
      title: t('tpl_dashboard.status'),
      dataIndex: 'disableAt',
      key: 'disableAt',
      render: (disableAt: boolean) =>
        disableAt !== null ? (
          <span style={{ color: 'red' }}>DESHABILITADO</span>
        ) : (
          <span>HABILITADO</span>
        ),
    },
    {
      title: t('tpl_dashboard.actions'),
      key: 'acciones',
      align: 'center',
      render: (record: Vehicle) => {
        if (isVehicleTruck(record)) {
          return (
            <ActionsCellContainer>
              <Tooltip title={t('tpl_dashboard.updated')} placement="left">
                <div style={{ marginLeft: '20px' }}>
                  <EditOutlined
                    className="update"
                    onClick={() => {
                      handleUpdateDataTruck(record.id)
                      setIsEditTruckModalOpen(true)
                    }}
                  />
                </div>
              </Tooltip>
            </ActionsCellContainer>
          )
        }
        return (
          <ActionsCellContainer>
            <Tooltip title={t('tpl_dashboard.updated')} placement="left">
              <div style={{ marginLeft: '20px' }}>
                <EditOutlined
                  className="update"
                  onClick={() => {
                    handleUpdateDataTrailer(record.id)
                    setIsEditTrailerModalOpen(true)
                  }}
                />
              </div>
            </Tooltip>
          </ActionsCellContainer>
        )
      },
    },
  ]
  const columnsDrivers: ColumnsType = [
    {
      title: t('tpl_dashboard.name'),
      dataIndex: 'firstName',
      key: 'firstName',
    },
    {
      title: t('tpl_dashboard.last_name'),
      dataIndex: 'lastName',
      key: 'lastName',
    },
    {
      title: t('tpl_dashboard.ID'),
      dataIndex: 'stateIdNumber',
      key: 'stateIdNumber',
    },
    {
      title: t('tpl_dashboard.phone'),
      dataIndex: 'phone',
      width: '14.5%',
      key: 'phone',
      render: (phone: string, record: Driver) => (
        <Row style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ flex: 1 }}>{phone}</div>
          <Tooltip
            title={
              // eslint-disable-next-line no-nested-ternary
              record.errorSendingValidationMessage
                ? t('tpl_dashboard.error')
                : record.phoneVerifiedAt
                ? t('tpl_dashboard.validated')
                : t('tpl_dashboard.pending')
            }
            placement="right"
          >
            <span
              style={{
                // eslint-disable-next-line no-nested-ternary
                color: record.errorSendingValidationMessage
                  ? 'red'
                  : record.phoneVerifiedAt
                  ? 'green'
                  : 'yellow',
                fontSize: '4em',
                marginLeft: '15px',
                lineHeight: '0em',
              }}
            >
              &#x2022;
            </span>
          </Tooltip>
        </Row>
      ),
    },
    {
      title: t('tpl_dashboard.preferred_truck'),
      dataIndex: 'preferredTrucks',
      key: 'preferredTruck',
      render: (preferredTrucks?: Truck[]) => {
        if (!preferredTrucks?.length) return t('globals.generics.has_not')
        const truck = preferredTrucks[0]

        return truck.equipmentType
          ? `${truck.licensePlate} - ${truck.equipmentType.name} - ${truck.description}`
          : `${truck.licensePlate} - ${truck.description}`
      },
    },
    {
      title: t('tpl_dashboard.preferred_trailer'),
      dataIndex: 'preferredTrailers',
      key: 'preferredTrailer',
      render: (preferredTrailers?: Trailer[]) => {
        if (!preferredTrailers?.length) return t('globals.generics.has_not')
        const trailer = preferredTrailers[0]

        return trailer.equipmentType
          ? `${trailer.licensePlate} - ${trailer.equipmentType.name} - ${trailer.description}`
          : `${trailer.licensePlate} - ${trailer.description}`
      },
    },
    {
      title: t('tpl_dashboard.status'),
      dataIndex: 'driverByCarrierData',
      key: 'enabled',
      render: (driverByCarrierData: any[]) => {
        const disableAt = driverByCarrierData[0]?.disableAt
        return disableAt === null ? (
          <span>HABILITADO</span>
        ) : (
          <span style={{ color: 'red' }}>DESHABILITADO</span>
        )
      },
    },
    {
      title: t('tpl_dashboard.actions'),
      key: 'acciones',
      align: 'center',
      render: (record: Driver) => (
        <ActionsCellContainer>
          <Tooltip title={t('tpl_dashboard.updated')} placement="left">
            <div style={{ marginLeft: '20px' }}>
              <EditOutlined
                className="update"
                onClick={() => {
                  setIsLoading(true)
                  handleUpdateDriverData(record.id)
                  setIsEditDriverModalOpen(true)
                  setTimeout(() => {
                    setIsLoading(false)
                  }, 500)
                }}
              />
            </div>
          </Tooltip>
        </ActionsCellContainer>
      ),
    },
  ]

  const columns = [
    {
      title: t('tpl_router.tpl_dashboard.columns.id'),
      dataIndex: 'id',
      key: 'id',
      render: (id: number, ticket: Ticket) => (
        <div className="priority-flag-container">
          <span>{id}</span>
          {!ticket.autoAssign && (
            <span className="priority-flag">{t('globals.generics.priority')}</span>
          )}
        </div>
      ),
    },
    {
      title: t('tpl_router.tpl_dashboard.columns.material'),
      dataIndex: 'material',
      key: 'id',
      render: (material: Material) => material && <>{material.nameShort}</>,
    },
    {
      title: t('tpl_router.tpl_dashboard.columns.equipment'),
      dataIndex: 'equipment',
      key: 'id',
      render: (equipment: Equipment) => (
        <Tag style={{ color: theme.textColor }} color={'#' + equipment.color}>
          {equipment.nameShort}
        </Tag>
      ),
    },
    {
      title: t('tpl_router.tpl_dashboard.columns.origin'),
      key: 'id',
      render: (ticket: Ticket) => locationBody(ticket, 'origen'),
    },
    {
      title: t('tpl_router.tpl_dashboard.columns.destination'),
      key: 'destination',
      render: (ticket: Ticket) => locationBody(ticket, 'destino'),
    },
    {
      title: t('tpl_router.tpl_dashboard.columns.supplier'),
      dataIndex: 'supplier',
      key: 'id',
      render: ({ businessName }: { businessName: string }, ticket: Ticket) => (
        <div>
          {businessName}
          <br />
          {ticket.comments && (
            <StyledLink
              onClick={() => {
                seeComments(ticket.comments)
              }}
            >
              ({t('tpl_router.tpl_dashboard.columns.comments')})
            </StyledLink>
          )}
        </div>
      ),
    },
    {
      title: t('tpl_router.tpl_dashboard.columns.withdrawal_date'),
      dataIndex: 'withdrawalDate',
      key: 'withdrawalDate',
      render: (withdrawalDate: Date) =>
        withdrawalDate && <>{dayjs(withdrawalDate).format(dateHourFormat)}</>,
    },
    {
      title: t('tpl_router.tpl_dashboard.columns.status'),
      dataIndex: 'status',
      key: 'id',
      render: ({ name }: { name: string }) =>
        name === StatusTicketEnum.PENDING ? (
          <>{t('tpl_router.filterOptions.pending')}</>
        ) : (
          <>{t(`globals.generics.status.${name.toLowerCase()}`)}</>
        ),
    },
    {
      title: t('tpl_router.tpl_dashboard.columns.actions'),
      render: (ticket: Ticket) => actionButtons(ticket),
    },
  ]

  const handleDeclineTicket = (payload: { reason: string }, reset: () => void) => {
    ticketNotification.setState(ticketToEdit)
    declineTicket({ id: ticketToEdit!, payload })
    reset()
  }

  const handleAcceptTicket = (
    { description }: Observation,
    driver: number,
    truck: number,
    trailer: number,
  ) => {
    ticketNotification.setState(ticketToEdit)
    acceptTicket({
      id: ticketToEdit!,
      note: { description },
      driverId: driver,
      truckId: truck,
      trailerId: trailer,
    })
  }

  const handleAddAditionalDataTicket = async (payload: UpdateTicket) => {
    setAddAdditionalDataLoading(true)

    try {
      ticketNotification.setState(ticketToEdit)
      await updateTicket(ticketToEdit, payload)
      setIsAddAditionalDataTicketModalOpen(false)
    } catch (error) {
      console.error('Error al actualizar ticket:', error)
    } finally {
      setAddAdditionalDataLoading(false)
    }
  }

  const handleAcceptTruck = (data: Truck) => {
    if (!isLoadingTruck) {
      createTruck(data)
    }
    setIsAddTruckModalOpen(false)
  }

  const handleAcceptTrailer = async (data: Trailer) => {
    if (!isLoadingTrailer) {
      await createTrailer(data)
    }
    setIsAddTrailerModal(false)
  }

  const handleEditTruck = async (data: UpdateTruckData) => {
    if (idEditDataTruck !== undefined) {
      try {
        await updateTruck(idEditDataTruck, data)
      } catch (error) {
        console.log(error)
      } finally {
        setUpdateDataTrucks([])
      }
    }
    setIsEditTruckModalOpen(false)
  }

  const handleEditTrailer = async (data: UpdateTruckData) => {
    if (idEditDataTrailer !== undefined) {
      try {
        await updateTrailer(idEditDataTrailer, data)
      } catch (error) {
        console.log(error)
      } finally {
        setUpdateDataTrailers([])
      }
    }
    setIsEditTrailerModalOpen(false)
  }

  const handleUpdateDataTruck = (truckId: number) => {
    const truckToUpdate = carrierTrucks.find(item => item.id === truckId)
    if (truckToUpdate !== undefined) {
      setUpdateDataTrucks([truckToUpdate])
      setIdEditDataTruck(truckId)
    }
  }

  const handleUpdateDataTrailer = (trailerId: number) => {
    const trailerToUpdate = carrierTrailers.find(item => item.id === trailerId)
    if (trailerToUpdate !== undefined) {
      setUpdateDataTrailers([trailerToUpdate])
      setIdEditDataTrailer(trailerId)
    }
  }

  const handleAcceptDriver = (data: Driver) => {
    return new Promise((resolve, reject) => {
      createDriver(data, {
        onSuccess: () => {
          setIsAddDriverModalOpen(false)
          resolve(true)
        },
        onError: error => {
          reject(error)
        },
      })
    })
  }

  const handleEditDriver = async (data: UpdateDriverData) => {
    if (idEditDataDriver !== undefined) {
      try {
        await updateDriver(idEditDataDriver, data)
        queryClient.fetchQuery(QUERY_KEYS.USER)
      } catch (error) {
        console.log(error)
      } finally {
        setUpdateDataDriver(undefined)
        setIdEditDataDriver(undefined)
      }
    }
    setIsEditDriverModalOpen(false)
  }

  const handleUpdateDriverData = useCallback(
    (driverId: number) => {
      const driverToUpdate = driversData?.find(item => item.id === driverId)
      if (driverToUpdate !== undefined) {
        setUpdateDataDriver(driverToUpdate)
        setIdEditDataDriver(driverId)
      }
    },
    [driversData],
  )

  const filterOptions = [
    {
      label: `${t('tpl_router.filterOptions.pending')}`,
      value: PENDING,
    },
    {
      label: `${t('tpl_router.filterOptions.confirmed')}`,
      value: CONFIRMED,
    },
    {
      label: `${t('tpl_router.filterOptions.transit')}`,
      value: TRANSIT,
    },
    {
      label: `${t('tpl_router.filterOptions.delivered')}`,
      value: DELIVERED,
    },
    {
      label: `${t('globals.generics.status.no_show')}`,
      value: StatusTicketEnum.NO_SHOW,
    },
  ]

  const orderByOptions = [
    {
      label: `${t('admin_router.filter_options.order_by.ascending')}`,
      value: 'ASC',
    },
    {
      label: `${t('admin_router.filter_options.order_by.descending')}`,
      value: 'DESC',
    },
  ]

  const handleStateChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      statusName: [...(value as string[])],
    }))
  }

  const handleOrderByFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      orderBy: value as string,
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }

  const handleCleanFilters = () => {
    setTicketToSearch(undefined)
    handleSetRowData()
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      carrierId: [],
      carrierMode: [],
      statusName: window.location.pathname === HOME ? ['PENDING'] : [],
      supplierId: [],
      equipmentId: [],
      orderBy: undefined,
      ticketId: undefined,
      materialId: undefined,
    }))
    resetFilters()
    queryClient.resetQueries(QUERY_KEYS.TICKETS)
  }

  const handleMaterialFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      materialId: [...(value as number[])],
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }
  const handleEquipmentFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      equipmentId: [...(value as number[])],
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }

  const deliveryNoteTemplate = (ticket: Ticket) => (
    <ActionButtonCustom
      className="invoice"
      onClick={() => {
        if (ticket.status.name !== StatusTicketEnum.PENDING) {
          setIsLoading(true)
          queryClient.removeQueries([QUERY_KEYS.TICKET])
          setTicketToEdit(ticket.id)
          setTimeout(() => {
            setIsLoading(false)
            setIsAddAditionalDataTicketModalOpen(true)
          }, 500)
          ticketNotification.setState(ticket.id)
        }
      }}
    >
      <DeliveryNote ticket={ticket} />
    </ActionButtonCustom>
  )

  const [expandedRows, setExpandedRows] = useState<readonly Key[]>([])

  const handleSetRowData: (expandedKeys?: readonly Key[]) => void = expandedKeys => {
    setExpandedRows(expandedKeys ?? [])
  }

  const handleCloseMapsModal = () => {
    setIsMapsModalOpen(false)
  }

  const handleCleanSearchInput = () => {
    setTicketToSearch(undefined)
  }

  const handleOpenMapsModal = (ticket: Ticket, title: string) => {
    setIsMapsModalOpen(true)
    if (title === 'origen') {
      setLocationLatitude(ticket.origin.location.coordinates[0])
      setLocationLongitude(ticket.origin.location.coordinates[1])
      setLocationStreet(ticket.origin.street)
      setLocationNumber(ticket.origin.number)
      setLocationState(ticket.origin.state)
      setLocationCity(ticket.origin.city)
      setTag(ticket.origin.tag)
    }
    if (title === 'destino') {
      setLocationLatitude(ticket.destination.location.coordinates[0])
      setLocationLongitude(ticket.destination.location.coordinates[1])
      setLocationStreet(ticket.destination.street)
      setLocationNumber(ticket.destination.number)
      setLocationState(ticket.destination.state)
      setLocationCity(ticket.destination.city)
      setTag(ticket.destination.tag)
    }
  }

  const handleSortChange = (value: any) => {
    setSortOrder(value)
  }

  const handleEnabledFilter = (value: any) => {
    setStatusFilter(value)
  }

  const sortedVehiclesData = useMemo(() => {
    const result = [...vehiclesData]

    if (vehicleSort === 'ascLicense') {
      result.sort((a, b) => a.licensePlate.localeCompare(b.licensePlate))
    } else if (vehicleSort === 'descLicense') {
      result.sort((a, b) => b.licensePlate.localeCompare(a.licensePlate))
    } else if (vehicleSort === 'ascDescription') {
      result.sort((a, b) => a.description.localeCompare(b.description))
    } else if (vehicleSort === 'descDescription') {
      result.sort((a, b) => b.description.localeCompare(a.description))
    }

    return result
  }, [vehiclesData, vehicleSort])

  const sortedDataDrivers = useMemo(() => {
    if (Array.isArray(sortOrder) && sortOrder.length > 0) {
      const sortOrderStrings = sortOrder.map(id => id.toString())
      return driversData?.filter(driver =>
        sortOrderStrings.includes(driver.preferredTruck?.id.toString()),
      )
    }
    if (sortOrder === 'ascFirstName') {
      return driversData?.slice().sort((a, b) => a.firstName.localeCompare(b.firstName))
    }
    if (sortOrder === 'descFirstName') {
      return driversData?.slice().sort((a, b) => b.firstName.localeCompare(a.firstName))
    }
    if (sortOrder === 'ascId') {
      return driversData?.slice().sort((a, b) => a.stateIdNumber.localeCompare(b.stateIdNumber))
    }
    if (sortOrder === 'descId') {
      return driversData?.slice().sort((a, b) => b.stateIdNumber.localeCompare(a.stateIdNumber))
    }
    return driversData
  }, [driversData, sortOrder])

  const rowClassName = (_record: unknown, index: number) =>
    index % 2 === 0 ? 'zebra-even' : 'zebra-odd'

  const locationBody = (ticket: Ticket, title: string) => (
    <Button
      type="link"
      style={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        textAlign: 'left',
        paddingLeft: '0',
        marginTop: 'min(20px, 40px)',
        marginBottom: 'min(20px, 40px)',
      }}
      onClick={() => {
        handleOpenMapsModal(ticket, title)
      }}
    >
      <span
        style={{
          whiteSpace: 'normal',
        }}
      >
        {title === 'origen' ? ticket.origin.tag : ticket.destination.tag}
      </span>
    </Button>
  )
  if (pathname === PATHS.CARRIER.VEHICLES) {
    return (
      <ContentContainer key="vehicles_dashboard">
        <StyledRow className="sticky-options">
          <div
            className={`filters-container${
              filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
            }`}
          >
            <Button onClick={handleDisplayFilters} id="displayFiltersButton">
              <FilterOutlined />
            </Button>
            <div className="filters-list">
              <Tooltip placement="bottomRight" title="Nueva Unidad Motriz">
                <StyledButton className="inline" onClick={() => setIsAddTruckModalOpen(true)}>
                  <p style={{ marginTop: 15, marginLeft: 1 }}>Nueva Unidad Motriz</p>
                </StyledButton>
              </Tooltip>
              <Tooltip placement="bottomRight" title="Nuevo Acoplado">
                <StyledButton className="inline" onClick={() => setIsAddTrailerModal(true)}>
                  <p style={{ marginTop: 15, marginLeft: 1 }}>Nuevo Acoplado</p>
                </StyledButton>
              </Tooltip>
              <StyledSelect
                maxTagCount="responsive"
                allowClear
                value={vehicleSort}
                placeholder={t('tpl_dashboard.sort_by_registration')}
                onChange={value => setVehicleSort(value as string | undefined)}
              >
                <Option value="ascLicense">Ordenar A-Z</Option>
                <Option value="descLicense">Ordenar Z-A</Option>
              </StyledSelect>
              <StyledSelect
                maxTagCount="responsive"
                allowClear
                value={vehicleSort}
                placeholder={t('tpl_dashboard.sort_by_description')}
                onChange={value => setVehicleSort(value as string | undefined)}
              >
                <Option value="ascDescription">Ordenar A-Z</Option>
                <Option value="descDescription">Ordenar Z-A</Option>
              </StyledSelect>
              <StyledSelect
                maxTagCount="responsive"
                mode="multiple"
                allowClear
                value={vehicleEquipmentFilter}
                placeholder={t('tpl_dashboard.equipment')}
                onChange={value => setVehicleEquipmentFilter((value as number[] | undefined) ?? [])}
              >
                {equipmentOptions?.map(equipment => (
                  <Option key={equipment.value} value={equipment.value}>
                    {equipment.label}
                  </Option>
                ))}
              </StyledSelect>
              <StyledSelect
                maxTagCount="responsive"
                allowClear
                value={vehicleKindFilter}
                placeholder={t('tpl_dashboard.drive_unit_coupled')}
                onChange={value => setVehicleKindFilter(value as VehicleKindFilter | undefined)}
              >
                <Option value="trailer">Acoplado</Option>
                <Option value="truck">Unidad Motriz</Option>
              </StyledSelect>
              <StyledSelect
                maxTagCount="responsive"
                allowClear
                value={statusFilter}
                placeholder={t('tpl_dashboard.state')}
                onChange={handleEnabledFilter}
              >
                <Option value={true}>{t('globals.generics.enabled')}</Option>
                <Option value={false}>{t('globals.generics.disabled')}</Option>
              </StyledSelect>
            </div>
          </div>
        </StyledRow>
        <>
          <StyledRow className="ticket-handler">
            <div style={{ flex: 1 }} />
            {userIsLoading || carrierVehiclesLoading ? <Spin /> : <div style={{ flex: 1 }} />}
          </StyledRow>
          <Table
            columns={columnsVehicle}
            dataSource={sortedVehiclesData}
            loading={carrierVehiclesLoading}
            rowKey={({ id }) => `vehicles-${id}-${statusFilter}`}
            rowClassName={rowClassName}
            pagination={false}
          />
        </>
        {isAddTruckModalOpen && (
          <AddTruckModal
            open={isAddTruckModalOpen}
            onClose={() => setIsAddTruckModalOpen(false)}
            onAdd={handleAcceptTruck}
          />
        )}
        {!!isEditTruckModalOpen && updateDataTrucks && updateDataTrucks[0] && (
          <EditTruckModal
            open={isEditTruckModalOpen}
            onClose={() => setIsEditTruckModalOpen(false)}
            onEdit={handleEditTruck}
            truckData={updateDataTrucks[0]}
          />
        )}
        <AddTrailerModal
          open={isAddTrailerModal}
          onClose={() => setIsAddTrailerModal(false)}
          onAdd={handleAcceptTrailer}
        />
        <EditTrailerModal
          open={isEditTrailerModalOpen}
          onClose={() => setIsEditTrailerModalOpen(false)}
          onEdit={handleEditTrailer}
          data={updateDataTrailers}
        />
      </ContentContainer>
    )
  }
  if (pathname === PATHS.CARRIER.DRIVERS) {
    return (
      <ContentContainer key="drivers_dashboard">
        <StyledRow className="sticky-options">
          <div
            className={`filters-container${
              filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
            }`}
          >
            <Button onClick={handleDisplayFilters} id="displayFiltersButton">
              <FilterOutlined />
            </Button>
            <div className="filters-list">
              <Tooltip placement="bottomRight" title="Nuevo chofer">
                <StyledButton className="inline" onClick={() => setIsAddDriverModalOpen(true)}>
                  <p style={{ marginTop: 15, marginLeft: 1 }}>Nuevo chofer</p>
                </StyledButton>
              </Tooltip>
              <StyledSelect
                maxTagCount="responsive"
                allowClear
                placeholder={t('tpl_dashboard.sort_by_name')}
                onChange={handleSortChange}
              >
                <Option value="ascFirstName">Ordenar A-Z</Option>
                <Option value="descFirstName">Ordenar Z-A</Option>
              </StyledSelect>
              <StyledSelect
                maxTagCount="responsive"
                allowClear
                placeholder={t('tpl_dashboard.sort_by_ID')}
                onChange={handleSortChange}
              >
                <Option value="ascId">Ordenar 1-9</Option>
                <Option value="descId">Ordenar 9-1</Option>
              </StyledSelect>
              <StyledSelect
                maxTagCount="responsive"
                allowClear
                value={statusFilter}
                placeholder={t('tpl_dashboard.state')}
                onChange={handleEnabledFilter}
              >
                <Option value={true}>{t('globals.generics.enabled')}</Option>
                <Option value={false}>{t('globals.generics.disabled')}</Option>
              </StyledSelect>
            </div>
          </div>
        </StyledRow>
        <>
          <StyledRow className="ticket-handler">
            <div style={{ flex: 1 }} />
            {userIsLoading ? <Spin /> : <div style={{ flex: 1 }} />}
          </StyledRow>
          <Table
            columns={columnsDrivers}
            dataSource={sortedDataDrivers?.filter(driver => {
              if (statusFilter === undefined) {
                return true // Si no hay filtro, muestra todos
              }

              const disableAt = driver.driverByCarrierData[0]?.disableAt // Obtener el valor de disableAt

              // Si statusFilter es true, mostrar solo los que tienen disableAt no nulo
              // Si statusFilter es false, mostrar solo los que tienen disableAt nulo
              return statusFilter ? disableAt === null : disableAt !== null
            })}
            loading={userIsLoading || userFetching}
            // eslint-disable-next-line prettier/prettier
            rowKey={record => `${record.type}-${record.licensePlate}-${record.someOtherField}`}
            rowClassName={rowClassName}
            pagination={false}
          />
        </>
        <AddDriverModal
          open={isAddDriverModalOpen}
          onClose={() => setIsAddDriverModalOpen(false)}
          onAdd={handleAcceptDriver}
        />
        {isEditDriverModalOpen && updateDataDriver && (
          <EditDriverModal
            open={isEditDriverModalOpen}
            onClose={() => setIsEditDriverModalOpen(false)}
            onEdit={handleEditDriver}
            driver={updateDataDriver}
          />
        )}
      </ContentContainer>
    )
  }
  return (
    <ContentContainer key="tpl_dashboard">
      <StyledRow className="sticky-options">
        <div
          className={`filters-container${
            filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
          }`}
        >
          <Button onClick={handleDisplayFilters} id="displayFiltersButton">
            <FilterOutlined />
          </Button>
          <div className="filters-list">
            <StyledButton className="clean-tpl option" onClick={handleCleanFilters}>
              {t('globals.generics.clean_filters')}
            </StyledButton>

            {pathname.includes('all') || pathname.includes('evouchers') ? (
              <StyledSelect
                mode="multiple"
                maxTagCount="responsive"
                allowClear
                filterOption={handleFilterOptions}
                placeholder={`${t('tpl_router.tpl_dashboard.filters.status')}`}
                options={
                  pathname.includes('evouchers')
                    ? filterOptions.filter(item => item.value !== PENDING)
                    : filterOptions
                }
                onChange={handleStateChange}
                value={filters?.statusName}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
            ) : null}
            <StyledSelect
              maxTagCount="responsive"
              mode="multiple"
              allowClear
              filterOption={handleFilterOptions}
              placeholder={`${t('tpl_router.tpl_dashboard.filters.material')}`}
              options={materialsOptions}
              onChange={handleMaterialFilterChange}
              value={filters?.materialId}
              onDropdownVisibleChange={handleCleanSearchInput}
            />
            <StyledSelect
              maxTagCount="responsive"
              mode="multiple"
              allowClear
              filterOption={handleFilterOptions}
              placeholder={`${t('tpl_router.tpl_dashboard.filters.equipment')}`}
              options={equipmentOptions}
              onChange={handleEquipmentFilterChange}
              value={filters?.equipmentId}
              onDropdownVisibleChange={handleCleanSearchInput}
            />
            <StyledSelect
              maxTagCount="responsive"
              allowClear
              placeholder={t('suppliers_router.filter_options.order_by.title')}
              options={orderByOptions}
              onChange={handleOrderByFilterChange}
              value={filters?.orderBy}
              onDropdownVisibleChange={handleCleanSearchInput}
            />
          </div>
        </div>
        {pathname.includes('evouchers') || pathname.includes('all') ? (
          <CollapseExpand handleSetRowData={handleSetRowData} preparedData={preparedData} />
        ) : null}
      </StyledRow>
      {byDayPaths.includes(pathname) ? (
        <>
          <StyledRow className="ticket-handler">
            <div style={{ flex: 1 }} />
            <DateFromToFilter
              time={pathname === PATHS.CARRIER.EVOUCHERS ? TIME.EXPIRED : TIME.CURRENT}
            />
            {ticketsIsLoading ? <Spin /> : <div style={{ flex: 1 }} />}
          </StyledRow>
          <StyledRow className="data-table-container">
            <ExpandableTable
              expandedRows={expandedRows}
              handleSetRowData={handleSetRowData}
              data={preparedData}
              columns={[
                {
                  title: 'ID',
                  dataIndex: 'id',
                  key: 'id',
                  className: 'custom-column-3',
                  render: (id: number, ticket: Ticket) => (
                    <div className="priority-flag-container">
                      <span>{id}</span>
                      {!ticket.autoAssign && (
                        <span className="priority-flag">{t('globals.generics.priority')}</span>
                      )}
                    </div>
                  ),
                },
                {
                  title: t('tpl_dashboard.deliveryNote'),
                  dataIndex: 'deliveryNote',
                  key: 'deliveryNote',
                  render: (value, ticket) => deliveryNoteTemplate(ticket),
                  className: 'custom-column',
                },
                {
                  title: t('tpl_dashboard.material'),
                  dataIndex: ['material', 'nameShort'],
                  className: 'custom-column-3',
                },
                {
                  title: t('tpl_router.tpl_dashboard.columns.equipment'),
                  dataIndex: 'equipment',
                  key: 'id',
                  render: (equipment: Equipment) => (
                    <Tag style={{ color: theme.textColor }} color={'#' + equipment.color}>
                      {equipment.nameShort}
                    </Tag>
                  ),
                  className: 'custom-column-2',
                },
                {
                  title: t('tpl_dashboard.origin'),
                  render: (ticket: Ticket) => locationBody(ticket, 'origen'),
                  className: 'custom-column',
                },
                {
                  title: t('tpl_dashboard.destination'),
                  render: (ticket: Ticket) => locationBody(ticket, 'destino'),
                  className: 'custom-column',
                },
                {
                  title: t('tpl_dashboard.supplier'),
                  dataIndex: ['supplier', 'businessName'],
                  className: 'custom-column-3',
                },
                {
                  title: t('tpl_dashboard.withdrawalTime'),
                  dataIndex: 'withdrawalTime',
                  sorter: true, // Ant Design uses 'sorter' for sortable columns
                  className: 'custom-column',
                },
                {
                  title: t('tpl_dashboard.status'),
                  dataIndex: ['status', 'name'],
                  render: (_value, { status }) => {
                    const statusName = status.name

                    return (
                      <>
                        {statusName === StatusTicketEnum.PENDING
                          ? `${t('tpl_router.filterOptions.pending')}`
                          : t(`globals.generics.status.${statusName.toLowerCase()}`)}

                        {(statusName === 'CONFIRMED' || statusName === 'TRANSIT') && (
                          <Tooltip
                            title={t(
                              'notifications.the_trip_will_be_delivered_when_the_sap_ticket_is_validated.',
                            )}
                          >
                            <QuestionCircleOutlined style={{ marginLeft: '2rem' }} />
                          </Tooltip>
                        )}
                      </>
                    )
                  },
                  className: 'custom-column-2',
                },
                {
                  title: t('tpl_dashboard.driver'),
                  dataIndex: ['driver', 'firstName'],
                  render: (_text, ticket) => <DriverBarlight ticket={ticket} />,
                  className: 'custom-column',
                },
                {
                  title: t('tpl_router.tpl_dashboard.columns.actions'),
                  render: (ticket: Ticket) => actionButtons(ticket),
                  className: 'custom-column',
                },
              ]}
            />
          </StyledRow>
        </>
      ) : (
        <Table
          size="small"
          columns={columns}
          dataSource={tickets?.tickets}
          loading={ticketsIsLoading}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            current: paginationTickets.current,
            pageSize: filters?.limit,
            total: paginationTickets.total,
            onChange: (page, pageSize) => {
              setFilters({
                ...filters,
                limit: pageSize ?? 5,
                page,
              })
            },
          }}
        />
      )}
      {isAcceptTicketModalOpen && (
        <AcceptTicketModal
          open={isAcceptTicketModalOpen}
          onClose={() => setIsAcceptTicketModalOpen(false)}
          onOk={handleAcceptTicket}
          loading={acceptTicketLoading}
          requireTruckAndTrailer={requireTruckAndTrailer}
          requireDriver={requireDriver}
        />
      )}
      <DeclineTicketModal
        open={isDeclineTicketModalOpen}
        onClose={() => setIsDeclineTicketModalOpen(false)}
        onOk={handleDeclineTicket}
        loading={declineTicketLoading}
      />
      {isAddAditionalDataTicketModalOpen && !!ticketById?.ticket && (
        <AddAdditionalDataTicketModal
          ticket={ticketById.ticket}
          open={isAddAditionalDataTicketModalOpen}
          onClose={() => setIsAddAditionalDataTicketModalOpen(false)}
          onOk={handleAddAditionalDataTicket}
          loading={addAdditionalDataLoading}
          // requireTruckAndTrailer={requireTruckAndTrailer}
        />
      )}
      <MapsModal
        open={isMapsModalOpen}
        longitude={locationLongitude}
        latitude={locationLatitude}
        street={locationStreet}
        number={locationNumber}
        province={locationState}
        city={locationCity}
        close={handleCloseMapsModal}
        tag={tag}
      />
      {(isTicketByIdLoading || isLoading) &&
        !isDeclineTicketModalOpen &&
        !isAcceptTicketModalOpen && (
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9999,
            }}
          >
            <Spin size="large" />
          </div>
        )}
    </ContentContainer>
  )
}

export { TplDashboard }
